import {
    RegistrationInfo,
    ThreeDsAuthService,
    ThreeDsLookupResponse,
    ThreeDsSetupResponse,
    ThreeDsValidateResponse,
    type ThreeDsAuthConfig,
} from '@financial-auth/3ds'

import {formatDateFns} from '@shared/helpers/date'

import getApi, {getCommonBaseUrl} from '@web/helpers/api'
import {Sentry} from '@web/helpers/log'

const api = getApi(getCommonBaseUrl())

const endPoints: ThreeDsAuthConfig['endpoints'] = {
    async setupFn(regInfo: RegistrationInfo) {
        const {
            data: {result},
        } = await api.post<ApigwResponseBase<ThreeDsSetupResponse>>('/threeDS/setup', regInfo)
        return result
    },
    async lookupFn(params) {
        const {
            data: {result},
        } = await api.post<ApigwResponseBase<ThreeDsLookupResponse>>('/threeDS/lookup', params)
        return result
    },
    async validateFn(params) {
        const {
            data: {result},
        } = await api.post<ApigwResponseBase<ThreeDsValidateResponse>>('/threeDS/validate', params)
        return result
    },
}

/** 3ds(threeDS) 인증 절차 수행 및 결과 반환 helper */
export const checkThreeDs = ({
    /** 3ds에서 secureSessionId는 cardId */
    cardInfo: {cardId: secureSessionId, cardCompanyCode},
    fdsInfo: {fdsUuid},
}: {
    cardInfo: {
        cardId?: string
        cardCompanyCode?: string
        cardNumber?: string
    }
    fdsInfo: {fdsUuid?: string}
}) => {
    return new Promise<{sessionKey: Maybe<string>}>((resolve, reject) => {
        if (!secureSessionId) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return reject({
                code: 'THREE_DS_AUTH_FAIL',
                message: '카드 아이디가 없습니다',
            })
        }

        /** service 생성을 위한 설정 정보 */
        const config: ThreeDsAuthConfig = {
            endpoints: endPoints,
            callbacks: {
                onError: (error) => {
                    // eslint-disable-next-line prefer-promise-reject-errors
                    reject({
                        code: 'THREE_DS_AUTH_FAIL',
                        message: error,
                    })
                },
                /** 3ds 인증 완료(성공/실패)되면 호출되는 callback */
                async onAuthComplete(success, response) {
                    const code = response && 'code' in response ? response.code : undefined
                    const result = response && 'result' in response ? response.result : undefined

                    /**
                     * 로깅용 api 호출 (응답값 필요없음)
                     * apply 페이지로 넘어갈때 api 요청 cancel되므로 await 추가
                     */
                    try {
                        await api.post('/auth/identity/log', {
                            authenticationDateTime: formatDateFns(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
                            method: '3DS',
                            type: 'FDS',
                            result: success ? 'SUCCESS' : 'FAIL',
                            resultDetail: code,
                            value: secureSessionId,
                            corporation: cardCompanyCode,
                            fdsUuid,
                        })
                    } catch (error) {
                        Sentry.sendGeneralError(new Error('3ds 인증 로깅 실패'), error)
                    }

                    if (success) {
                        resolve({sessionKey: result?.sessionKey ?? null})
                    } else {
                        // eslint-disable-next-line prefer-promise-reject-errors
                        reject({
                            code: 'THREE_DS_AUTH_FAIL',
                            message: code ?? '3ds 인증 실패',
                        })
                    }
                },
            },
        }

        const threeDsAuthService = new ThreeDsAuthService(config)

        threeDsAuthService.startAuthFlow({
            secureSessionId,
            /** 결제 인증은 T3 (T1, T2는 주문서에서 처리하는 케이스 없으므로 T3 고정) */
            sessionType: 'T3',
        })
    })
}
